import { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import { useAuth } from "contexts/AuthContext";
import { leaseService } from "services/firestoreService";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";

function Leases() {
  const { user } = useAuth();
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!user) return;
    const fetchLeases = async () => {
      try {
        const data = await leaseService.getAll(user.uid);
        setLeases(data);
      } catch (err) {
        console.error("Failed to fetch leases:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLeases();
  }, [user]);
  
  // Filtered leases based on status and search query
  const filteredLeases = leases.filter(lease => {
    const matchesStatus = statusFilter === "All" || lease.status === statusFilter;
    const matchesSearch = 
      lease.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lease.property.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lease.tenant.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case "Active": return "success";
      case "Pending Renewal": return "warning";
      case "Expiring Soon": return "error";
      default: return "default";
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card sx={{ overflow: "visible" }}>
          <ArgonBox p={3}>
            <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <ArgonBox>
                <ArgonTypography variant="h4" fontWeight="medium">
                  Lease Agreements
                </ArgonTypography>
                <ArgonTypography variant="body2" color="text">
                  Manage signed lease agreements and view associated files.
                </ArgonTypography>
              </ArgonBox>
              <ArgonButton color="primary" variant="contained" startIcon={<Icon>add</Icon>}>
                New Lease
              </ArgonButton>
            </ArgonBox>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={tabValue} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
                <Tab label="All Leases" />
                <Tab label="Residential" />
                <Tab label="Commercial" />
                <Tab label="Industrial" />
              </Tabs>
            </Box>

            {/* Filters */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} md={6} lg={4}>
                <ArgonInput
                  placeholder="Search lease ID, property, or tenant..."
                  icon={{ component: "search", direction: "left" }}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3} lg={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="All">All Statuses</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Pending Renewal">Pending Renewal</MenuItem>
                    <MenuItem value="Expiring Soon">Expiring Soon</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} lg={2}>
                <FormControl fullWidth>
                  <InputLabel>Sort By</InputLabel>
                  <Select defaultValue="updated">
                    <MenuItem value="updated">Last Updated</MenuItem>
                    <MenuItem value="endDate">End Date</MenuItem>
                    <MenuItem value="rent">Monthly Rent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={12} lg={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <ArgonButton color="secondary" variant="outlined" startIcon={<Icon>filter_list</Icon>} sx={{ mr: 1 }}>
                  More Filters
                </ArgonButton>
                <ArgonButton color="info" variant="outlined" startIcon={<Icon>download</Icon>}>
                  Export
                </ArgonButton>
              </Grid>
            </Grid>

            {/* Lease Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Lease ID</TableCell>
                    <TableCell>Property</TableCell>
                    <TableCell>Tenant</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell align="right">Monthly Rent</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Documents</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLeases.map((lease) => (
                    <TableRow key={lease.id} hover>
                      <TableCell>
                        <ArgonTypography variant="caption" fontWeight="medium">
                          {lease.id}
                        </ArgonTypography>
                      </TableCell>
                      <TableCell>
                        <ArgonTypography variant="caption" fontWeight="medium">
                          {lease.property}
                        </ArgonTypography>
                      </TableCell>
                      <TableCell>{lease.tenant}</TableCell>
                      <TableCell>
                        <ArgonTypography variant="caption" component="div">
                          {lease.startDate} - {lease.endDate}
                        </ArgonTypography>
                      </TableCell>
                      <TableCell align="right">${lease.monthlyRent?.toLocaleString() || '0'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={lease.status} 
                          color={getStatusColor(lease.status)} 
                          size="small"
                          variant="contained"
                        />
                      </TableCell>
                      <TableCell>
                        <ArgonBox display="flex" alignItems="center">
                          <Icon fontSize="small" sx={{ color: "text.secondary", mr: 0.5 }}>description</Icon>
                          <ArgonTypography variant="caption">{lease.documents}</ArgonTypography>
                        </ArgonBox>
                      </TableCell>
                      <TableCell>{lease.lastUpdated}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton size="small">
                            <Icon fontSize="small">visibility</Icon>
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small">
                            <Icon fontSize="small">edit</Icon>
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton size="small">
                            <Icon fontSize="small">download</Icon>
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mt={3}>
              <ArgonTypography variant="caption" color="text">
                Showing {filteredLeases.length} of {leases.length} leases
              </ArgonTypography>
              <Stack spacing={2}>
                <Pagination count={1} color="primary" size="small" />
              </Stack>
            </ArgonBox>
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Leases;